import { useEffect, useState } from 'react';

/**
 * A Date that updates on a fixed interval.
 *
 * Kept as its own hook so the component that shows a ticking clock is the only
 * thing that re-renders. In the old app a clock ticked in the page-level
 * component, so every second it re-rendered several children that each rebuilt
 * large derived structures from scratch.
 *
 * Audit references prevented here: calendar.jsx #9, calendar.jsx #17,
 * calories.jsx #10.
 *
 * @param {number} intervalMs  How often to advance. Must be positive.
 * @returns {Date} The current instant, replaced each interval.
 * @throws {RangeError} if the interval is not a positive finite number.
 */
export function useClockTick(intervalMs) {
  if (!Number.isFinite(intervalMs) || intervalMs <= 0) {
    throw new RangeError(`useClockTick needs a positive interval, received ${intervalMs}`);
  }

  const [instant, setInstant] = useState(() => new Date());

  useEffect(() => {
    const timer = setInterval(() => setInstant(new Date()), intervalMs);
    return () => clearInterval(timer);
  }, [intervalMs]);

  return instant;
}
